/**
 * documents_prod — Dashboard pro production document parser modul.
 * Ukazuje volume, latence, status breakdown a anomálie z audit_log.
 */
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '../../lib/i18n';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
  RefreshButton,
  StatCard,
  StatsGrid,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../../components/ui';
import { Activity, AlertCircle, AlertTriangle, BookOpen, CheckCircle, Clock, FileText, LayoutDashboard, Search, XCircle } from 'lucide-react';
import { api } from '../../lib/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ToolStats {
  tool: string;
  category: 'text' | 'vision' | 'api';
  count: number;
  avg_ms: number;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
  status: Record<string, number>;
  top_anomalies: Array<{ code: string; count: number }>;
}

interface RecentEntry {
  ts: string;
  tool: string;
  status: string;
  duration_ms: number | null;
  anomaly_codes: string[];
  bank_id?: string | null;
  doc_type?: string | null;
  ico?: string | null;
  holder?: string | null;
  /** Submitter — kdo dokument odeslal ke zpracování. Pro parse_tax_datovka =
   * jméno fyzické osoby (statutární orgán / prokurista) z DIS+/PMD. Pro ostatní
   * tooly = audit identity.entity_id (= ACM caller, např. Lovable agent). */
  submitter?: string | null;
  submitter_role?: string | null;
  identity?: Record<string, unknown> | null;
  data?: Record<string, unknown> | null;
}

interface LatencyByCat {
  count: number;
  avg_ms: number;
  p50_ms: number;
  p95_ms: number;
}

interface StatsResponse {
  window: '24h' | '7d' | '30d';
  total_calls: number;
  status_summary: Record<string, number>;
  latency_by_category: {
    text: LatencyByCat;
    vision: LatencyByCat;
    api: LatencyByCat;
  };
  by_tool: ToolStats[];
  recent: RecentEntry[];
  generated_at: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, 'green' | 'yellow' | 'red' | 'gray'> = {
  OK: 'green',
  WARN: 'yellow',
  ANOMALY: 'red',
  FAIL: 'red',
  UNKNOWN: 'gray',
};

const STATUS_ICONS: Record<string, JSX.Element> = {
  OK: <CheckCircle size={12} className='text-green-400' />,
  WARN: <AlertCircle size={12} className='text-yellow-400' />,
  ANOMALY: <AlertTriangle size={12} className='text-red-400' />,
  FAIL: <XCircle size={12} className='text-red-500' />,
};

function fmtMs(ms: number | null): string {
  if (ms === null || ms === undefined) return '–';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtAge(ts: string): string {
  const sec = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  const ago = i18n.t('documentsProd.agoSuffix', 'zpět');
  if (sec < 60) return `${sec}s ${ago}`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${ago}`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${ago}`;
  return `${Math.floor(sec / 86400)}d ${ago}`;
}

function toolDisplayName(tool: string): string {
  return tool.replace('documents_prod_', '').replace('documents_', '').replace('_', ' ');
}

// Preferovaná pole pro per-tool detail. Klíčové pole z toho co tool vrací
// do audit_log. Když pole chybí, prostě se nevykreslí. Raw JSON je vždy
// dostupný v details níže.
// Logical ordering: identifikace dokumentu → klíčová data → kvalita/metadata.
// Field names matchují AUDIT_LOG data shape (= co tool reálně zapisuje do
// audit_log.data, ne return shape toolu).
const TOOL_DETAIL_FIELDS: Record<string, string[]> = {
  parse_bank: [
    // Identifikace
    'bank_id', 'holder_name', 'account_number',
    // Období
    'period_from', 'period_to',
    // Zůstatky
    'opening', 'closing',
    // Aktivita
    'tx_count', 'detection_method',
    // Kvalita
    'confidence', 'duration_ms',
  ],
  parse_brki: [
    'subject_birth_number',
    'crif_rating',
    'total_contracts_summary', 'active_count', 'closed_count',
    'total_debt_kc', 'total_monthly_kc',
    'confidence', 'duration_ms',
  ],
  parse_tax: [
    'doc_type', 'form_id', 'tax_year',
    'taxpayer_id',
    'tax_base', 'tax_amount',
    'confidence', 'duration_ms',
  ],
  parse_tax_personal: [
    'tax_year', 'taxpayer_id',
    'form_id', 'form_version',
    'tax_base', 'tax_amount',
    'confidence', 'duration_ms',
  ],
  parse_tax_company: [
    'tax_year', 'taxpayer_id', 'company_name',
    'form_id', 'form_version',
    'tax_base', 'tax_amount',
    'confidence', 'duration_ms',
  ],
  parse_tax_datovka: [
    // Co se podávalo
    'layout', 'pisemnost_kind', 'tax_year',
    // Komu (subjekt)
    'subject_name', 'subject_ico',
    // Kdy + identifikátor podání
    'submission_date', 'podaci_cislo',
    // Kdo podal (= člověk který fyzicky odeslal)
    'submitter_name', 'submitter_role',
    'confidence', 'duration_ms',
  ],
  parse_lv: [
    'lv_number', 'cadastral_territory_code',
    'owners_count', 'parcels_count', 'assets_count',
    'ownership_kind', 'has_encumbrances',
    'confidence', 'duration_ms',
  ],
  parse_osvc_overview: [
    'year', 'taxpayer_id', 'insurer_type',
    'pojistne_celkem', 'vymerovaci_zaklad',
    'confidence', 'duration_ms',
  ],
  extract_id: [
    'doc_type', 'country',
    'holder_name', 'doc_number',
    'expiry_date',
    'rc_valid', 'mrz_valid', 'fraud_concerns_count',
    'reading_quality', 'confidence', 'duration_ms',
  ],
  ares_lookup: [
    'ico', 'name', 'subject_status', 'legal_form',
    'vat_registered',
    'name_match', 'similarity',
    'duration_ms',
  ],
  classify_vision: [
    'doc_type', 'doc_subtype',
    'supported', 'recommended_tool',
    'language', 'is_scan',
    'confidence', 'description',
    'vision_duration_ms', 'duration_ms',
  ],
};

function fmtFieldValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? '✓' : '✗';
  if (typeof v === 'number') return v.toLocaleString('cs-CZ');
  if (typeof v === 'string') return v.length > 200 ? v.slice(0, 200) + '…' : v;
  return JSON.stringify(v);
}

function ToolLatestDetail({ tool, recent }: { tool: string; recent: RecentEntry[] }) {
  const { t } = useTranslation();
  const entry = recent.find((r) => r.tool === tool);
  if (!entry || !entry.data) {
    return (
      <div className='text-xs text-gray-500 italic py-2'>
        {t(
          'documentsProd.noAuditEntry',
          'Pro tento tool nemáme žádnou audit entry v okně (nebo legacy entry bez `data` field).',
        )}
      </div>
    );
  }
  const d = entry.data;
  const preferred = TOOL_DETAIL_FIELDS[tool] ?? [];
  const fields = preferred.filter((k) => k in d);
  const otherKeys = Object.keys(d)
    .filter((k) => !preferred.includes(k) && !['status', 'anomaly_codes', 'duration_ms'].includes(k))
    .filter((k) => d[k] !== null && d[k] !== undefined && d[k] !== '');

  const docId = (d.document_id as string | undefined) ?? (d.bank_id as string | undefined) ?? (d.ico as string | undefined) ?? entry.holder ?? entry.doc_type ?? '—';

  return (
    <div className='space-y-3 py-2'>
      <div className='flex items-center justify-between flex-wrap gap-2'>
        <div className='text-xs text-gray-400'>
          <span className='text-gray-500'>{t('documentsProd.lastCallLabel', 'Poslední volání:')}</span>{' '}
          <span className='font-mono text-gray-300'>{fmtAge(entry.ts)}</span>
          {' · '}
          <span className='text-gray-500'>{t('documentsProd.statusLabelShort', 'status:')}</span>{' '}
          <Badge variant={STATUS_COLORS[entry.status] ?? 'gray'} className='text-[10px]'>
            {entry.status}
          </Badge>
          {entry.duration_ms !== null && (
            <>
              {' · '}
              <span className='text-gray-500'>{t('documentsProd.durLabelShort', 'dur:')}</span>{' '}
              <span className='font-mono text-gray-300'>{fmtMs(entry.duration_ms)}</span>
            </>
          )}
          {docId !== '—' && (
            <>
              {' · '}
              <span className='text-gray-500'>{t('documentsProd.idLabelShort', 'id:')}</span>{' '}
              <span className='font-mono text-gray-300'>{docId}</span>
            </>
          )}
          {entry.submitter && (
            <>
              {' · '}
              <span className='text-gray-500'>{t('documentsProd.submitterLabelShort', 'odeslal:')}</span>{' '}
              <span className='font-mono text-gray-300'>{entry.submitter}</span>
              {entry.submitter_role && (
                <span className='text-[10px] text-gray-500 ml-1'>({entry.submitter_role})</span>
              )}
            </>
          )}
        </div>
      </div>

      {fields.length > 0 && (
        <div className='grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5'>
          {fields.map((k) => (
            <div key={k} className='text-xs'>
              <div className='text-[10px] text-gray-500 font-mono'>{k}</div>
              <div className='text-gray-200 font-mono break-all' title={String(d[k])}>
                {fmtFieldValue(d[k])}
              </div>
            </div>
          ))}
        </div>
      )}

      {entry.anomaly_codes && entry.anomaly_codes.length > 0 && (
        <div className='text-xs'>
          <div className='text-[10px] text-gray-500 font-mono mb-1'>anomaly_codes</div>
          <div className='flex flex-wrap gap-1'>
            {entry.anomaly_codes.map((c) => (
              <Badge key={c} variant='red' className='font-mono text-[10px]'>
                {c}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {otherKeys.length > 0 && (
        <details className='text-[11px] text-gray-500'>
          <summary className='cursor-pointer hover:text-gray-300'>
            {t('documentsProd.otherFields', 'další pole ({{count}})', { count: otherKeys.length })}
          </summary>
          <div className='mt-2 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 pl-2 border-l border-gray-800'>
            {otherKeys.map((k) => (
              <div key={k}>
                <span className='text-gray-600 font-mono'>{k}:</span>{' '}
                <span className='text-gray-400 font-mono'>{fmtFieldValue(d[k])}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <details className='text-[11px] text-gray-500'>
        <summary className='cursor-pointer hover:text-gray-300'>
          {t('documentsProd.rawJson', 'raw JSON')}
        </summary>
        <pre className='mt-2 p-2 bg-black/40 rounded overflow-x-auto text-[10px] text-gray-400 max-h-96'>
          {JSON.stringify(d, null, 2)}
        </pre>
      </details>
    </div>
  );
}

// ── Status pie ────────────────────────────────────────────────────────────────

function StatusBar({ counts }: { counts: Record<string, number> }) {
  const { t } = useTranslation();
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  if (total === 0) return <span className='text-xs text-gray-500'>—</span>;
  const unknownCount = counts.UNKNOWN ?? 0;
  const knownTotal = total - unknownCount;
  const okCount = counts.OK ?? 0;

  if (knownTotal === 0 && unknownCount > 0) {
    return (
      <span
        className='text-xs text-gray-500'
        title={t(
          'documentsProd.unknownStatusTooltip',
          'Audit entries bez status ani confidence field (edge case). Backfill from confidence+needs_llm_fallback selhal.',
        )}
      >
        {t('documentsProd.unknownStatusCount', '? {{count}}× bez statusu', { count: unknownCount })}
      </span>
    );
  }

  const order = ['OK', 'WARN', 'ANOMALY', 'FAIL', 'UNKNOWN'];
  const segments = order
    .filter((s) => counts[s])
    .map((s) => ({ status: s, count: counts[s]!, pct: (counts[s]! / total) * 100 }));

  const successPct = knownTotal > 0 ? Math.round((okCount / knownTotal) * 100) : null;
  const successTitle = unknownCount > 0
    ? t('documentsProd.successRateTooltipWithLegacy', '{{ok}}/{{total}} validovaných OK (+ {{legacy}} legacy bez statusu)', {
      ok: okCount,
      total: knownTotal,
      legacy: unknownCount,
    })
    : t('documentsProd.successRateTooltip', '{{ok}}/{{total}} validovaných OK', {
      ok: okCount,
      total: knownTotal,
    });

  return (
    <div className='flex items-center gap-2'>
      {successPct !== null && (
        <span
          className={`text-xs font-mono w-10 text-right ${
            successPct >= 95 ? 'text-green-400' : successPct >= 80 ? 'text-yellow-400' : 'text-red-400'
          }`}
          title={successTitle}
        >
          {successPct}%
        </span>
      )}
      <div className='flex h-2 w-32 overflow-hidden rounded-full bg-gray-800'>
        {segments.map((s) => (
          <div
            key={s.status}
            className={
              s.status === 'OK' ? 'bg-green-500' :
              s.status === 'WARN' ? 'bg-yellow-500' :
              s.status === 'ANOMALY' ? 'bg-red-500' :
              s.status === 'FAIL' ? 'bg-red-700' : 'bg-gray-500'
            }
            style={{ width: `${s.pct}%` }}
            title={`${s.status}: ${s.count} (${s.pct.toFixed(0)}%)`}
          />
        ))}
      </div>
      <div className='flex gap-1 text-xs text-gray-400'>
        {segments.map((s) => (
          <span key={s.status} className='font-mono'>
            {STATUS_ICONS[s.status]} {s.count}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

interface ClassifyResult {
  doc_type: string;
  doc_subtype?: string | null;
  supported: boolean;
  recommended_tool: string | null;
  language?: string;
  is_scan?: boolean;
  page_count_estimate?: number;
  issuer_hint?: string | null;
  confidence: number;
  description?: string;
  vision_duration_ms?: number;
  duration_ms?: number;
  error?: string;
}

interface HistoryEntry {
  id: number;
  ts: number;
  fileName: string;
  fileSize: number;
  result: ClassifyResult | null;
  error: string | null;
}

// ── Connection info panel — kam volat + jak se autentizovat ───────────────────

function ConnectionInfoPanel() {
  const { t } = useTranslation();
  const publicMcp = 'https://501ef637.llm.services.maxcloud.cz/mcp';
  const publicApiext = 'https://501ef637.llm.services.maxcloud.cz/apiext/documents_prod';
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('documentsProd.connectionTitle', 'Connection — kam volat & auth')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='space-y-3 text-xs'>
          <div>
            <div className='text-[10px] uppercase text-gray-500 mb-1'>
              {t('documentsProd.mcpEndpointLabel', 'MCP JSON-RPC endpoint (= primární)')}
            </div>
            <code className='block px-2 py-1 rounded bg-gray-950 border border-gray-800 font-mono text-gray-200'>
              POST {publicMcp}
            </code>
            <div className='text-gray-500 mt-1'>
              {t('documentsProd.headerLabel', 'Header:')}{' '}
              <code className='text-gray-300'>x-api-key: &lt;Lovable Investoroom key&gt;</code>{' '}
              · Content-Type: <code className='text-gray-300'>application/json</code>
            </div>
            <div className='text-gray-500 mt-1'>
              {t('documentsProd.bodyLabel', 'Body:')}{' '}
              <code className='text-gray-300'>{'{"jsonrpc":"2.0","id":1,"method":"tools/call","params":{"name":"documents_prod.parse_bank","arguments":{...}}}'}</code>
            </div>
          </div>
          <div>
            <div className='text-[10px] uppercase text-gray-500 mb-1'>
              {t('documentsProd.restCatalogLabel', 'REST katalog parserů (= discovery, dynamic)')}
            </div>
            <code className='block px-2 py-1 rounded bg-gray-950 border border-gray-800 font-mono text-gray-200'>
              GET {publicApiext}/tools
            </code>
            <div className='text-gray-500 mt-1'>
              {t('documentsProd.restCatalogDesc', 'Vrátí všechny dostupné parsery s')}{' '}
              <code className='text-gray-300'>extracted_data_fields</code> {t('documentsProd.andWord', 'a')}{' '}
              <code className='text-gray-300'>output_top_fields</code>.
            </div>
          </div>
          <div>
            <div className='text-[10px] uppercase text-gray-500 mb-1'>
              {t('documentsProd.discoveryShapeLabel', 'Discovery shape (= co poslat / co dostat)')}
            </div>
            <code className='block px-2 py-1 rounded bg-gray-950 border border-gray-800 font-mono text-gray-200'>
              POST {publicMcp}  body=&#123;method:"tools/list"&#125;
            </code>
            <div className='text-gray-500 mt-1'>
              {t('documentsProd.perToolSpecLabel', 'Per-tool spec:')}{' '}
              <code className='text-gray-300'>name</code>, <code className='text-gray-300'>description</code>,{' '}
              <code className='text-gray-300'>inputSchema</code>, <code className='text-gray-300'>outputSchema</code>,{' '}
              {t('documentsProd.aliasesAnnotations', 'aliases, annotations.')}
            </div>
          </div>
          <div className='pt-2 border-t border-gray-800 text-[10px] text-gray-500'>
            <strong className='text-gray-400'>{t('documentsProd.scopeLabel', 'Scope:')}</strong>{' '}
            {t('documentsProd.scopeDesc1', 'každý tool vyžaduje')}{' '}
            <code className='text-gray-300'>documents_prod:read</code>{' '}
            {t('documentsProd.scopeDesc2', '(nebo :write/:admin). API klíče se generují v ACM admin UI — pro Investoroom dnes existuje key')}{' '}
            <code className='text-gray-300'>adx_inv_prod_***</code> {t('documentsProd.scopeDesc3', '(= prod scope).')}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Available parsers — katalog z /tools endpoint (dynamic) ───────────────────

interface AvailableTool {
  name: string;
  description?: string;
  doc_type: string;
  accepts: string[];
  note?: string;
  extracted_data_fields: string[];
  output_top_fields: string[];
}

interface AvailableToolsResponse {
  tools: AvailableTool[];
  total: number;
  with_metadata: number;
  schema_version: string;
  notes: string[];
  generated_at: string;
}

function AvailableParsersPanel() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showLegacy, setShowLegacy] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['documents_prod', 'tools_catalog'],
    queryFn: () =>
      api.mcp<AvailableToolsResponse>('documents_prod.tools_catalog', {}),
    refetchInterval: 60_000, // 1× za minutu — discovery se mění zřídka
  });

  if (isLoading) {
    return (
      <div className='text-sm text-gray-500'>
        {t('documentsProd.loadingCatalog', 'Načítám katalog parserů…')}
      </div>
    );
  }
  if (!data) return null;

  const production = data.tools.filter((tool) => tool.doc_type !== 'UNCLASSIFIED');
  const legacy = data.tools.filter((tool) => tool.doc_type === 'UNCLASSIFIED');

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between flex-wrap gap-2'>
          <CardTitle>
            {t('documentsProd.availableParsersTitle', 'Dostupné parsery')} ({data.with_metadata}{' '}
            {t('documentsProd.productionWord', 'production')}
            {legacy.length > 0 && (
              <span className='text-gray-500'>
                {' '}+ {legacy.length} {t('documentsProd.legacyWord', 'legacy')}
              </span>
            )})
          </CardTitle>
          <div className='text-[10px] text-gray-500'>
            {t('documentsProd.schemaInfo', 'schema {{version}} · dynamic katalog z loaderu', {
              version: data.schema_version,
            })}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className='space-y-1.5'>
          {production.map((tool) => {
            const isExp = expanded === tool.name;
            return (
              <div key={tool.name} className='rounded border border-gray-800 overflow-hidden'>
                <button
                  type='button'
                  onClick={() => setExpanded(isExp ? null : tool.name)}
                  className='w-full px-3 py-2 flex items-center justify-between gap-2 hover:bg-gray-900/30 text-left'
                >
                  <div className='flex items-center gap-3 min-w-0 flex-1'>
                    <span className='text-gray-500 text-xs w-3'>{isExp ? '▼' : '▶'}</span>
                    <code className='font-mono text-xs text-gray-200 truncate'>{tool.name}</code>
                    <Badge variant='gray' className='text-[10px] flex-shrink-0'>
                      {tool.doc_type}
                    </Badge>
                  </div>
                  <div className='text-[10px] text-gray-500 flex-shrink-0 font-mono'>
                    {tool.extracted_data_fields.length}f · {tool.output_top_fields.length}t
                  </div>
                </button>
                {isExp && (
                  <div className='px-3 py-3 bg-gray-900/40 border-t border-gray-800 space-y-3 text-xs'>
                    {tool.description && (
                      <div>
                        <div className='text-[10px] uppercase text-gray-500 mb-1'>
                          {t('documentsProd.descriptionLabel', 'Description')}
                        </div>
                        <div className='text-gray-300'>{tool.description}</div>
                      </div>
                    )}
                    {tool.note && (
                      <div className='text-gray-400 italic border-l-2 border-yellow-700/50 pl-2'>
                        {tool.note}
                      </div>
                    )}
                    <div>
                      <div className='text-[10px] uppercase text-gray-500 mb-1'>
                        {t('documentsProd.acceptsLabel', 'Accepts')}
                      </div>
                      <div className='flex flex-wrap gap-1'>
                        {tool.accepts.map((a) => (
                          <Badge key={a} variant='gray' className='text-[10px] font-mono'>{a}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className='text-[10px] uppercase text-gray-500 mb-1'>
                        extracted_data_fields ({tool.extracted_data_fields.length})
                      </div>
                      <div className='flex flex-wrap gap-1'>
                        {tool.extracted_data_fields.map((f) => (
                          <code key={f} className='font-mono text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-300'>
                            {f}
                          </code>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className='text-[10px] uppercase text-gray-500 mb-1'>
                        output_top_fields ({tool.output_top_fields.length})
                      </div>
                      <div className='flex flex-wrap gap-1'>
                        {tool.output_top_fields.map((f) => (
                          <code key={f} className='font-mono text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400'>
                            {f}
                          </code>
                        ))}
                      </div>
                    </div>
                    <details className='text-[11px] text-gray-500'>
                      <summary className='cursor-pointer hover:text-gray-300'>
                        {t('documentsProd.curlExample', 'Curl příklad')}
                      </summary>
                      <pre className='mt-2 p-2 bg-black/40 rounded overflow-x-auto text-[10px] text-gray-300'>
{`curl -X POST https://501ef637.llm.services.maxcloud.cz/mcp \\
  -H 'x-api-key: <Investoroom key>' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "jsonrpc":"2.0","id":1,"method":"tools/call",
    "params":{
      "name":"${tool.name}",
      "arguments":{${tool.accepts.includes('pdf_base64') ? '"pdf_base64":"<base64>"' : tool.accepts.includes('ico') ? '"ico":"12345678"' : '"data_url":"data:image/png;base64,..."'}}
    }
  }'`}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {legacy.length > 0 && (
          <div className='mt-4 pt-3 border-t border-gray-800'>
            <button
              type='button'
              onClick={() => setShowLegacy(!showLegacy)}
              className='text-[10px] uppercase text-gray-500 hover:text-gray-300'
            >
              {showLegacy ? '▼' : '▶'}{' '}
              {t('documentsProd.legacyUndocumented', 'Legacy / nedokumentované ({{count}})', {
                count: legacy.length,
              })}
            </button>
            {showLegacy && (
              <div className='mt-2 text-[11px] text-gray-500 space-y-1'>
                {legacy.map((tool) => (
                  <div key={tool.name} className='flex items-center gap-2'>
                    <code className='font-mono text-gray-400'>{tool.name}</code>
                    {tool.description && (
                      <span className='truncate'>— {tool.description.slice(0, 80)}…</span>
                    )}
                  </div>
                ))}
                <div className='text-[10px] text-gray-600 italic pt-1'>
                  {t(
                    'documentsProd.legacyNote',
                    'Tooly bez explicit metadata. Z pohledu Lovable agenta = nepoužívat pro production extrakci.',
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ClassifyVisionPanel() {
  const { t } = useTranslation();
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLoading(true);
    const id = Date.now();

    try {
      const buffer = await f.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = '';
      const chunkSize = 8192;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
      }
      const b64 = btoa(binary);
      const dataUrl = `data:${f.type || 'application/octet-stream'};base64,${b64}`;

      const r = await api.mcp<ClassifyResult>(
        'documents_prod.classify_vision',
        { data_url: dataUrl },
      ); // namespaced alias of documents_prod_classify_vision
      const entry: HistoryEntry = {
        id, ts: Date.now(), fileName: f.name, fileSize: f.size,
        result: r.error ? null : r,
        error: r.error ?? null,
      };
      setHistory((prev) => [entry, ...prev].slice(0, 3));
    } catch (e) {
      setHistory((prev) => [{
        id, ts: Date.now(), fileName: f.name, fileSize: f.size,
        result: null, error: (e as Error).message,
      }, ...prev].slice(0, 3));
    } finally {
      setLoading(false);
      e.target.value = ''; // reset, aby šel nahrát stejný soubor znovu
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <CardTitle>{t('documentsProd.sandboxTitle', 'Sandbox — classify_vision test')}</CardTitle>
          {history.length > 0 && (
            <button
              onClick={() => setHistory([])}
              className='text-[10px] px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-400 font-mono'
            >
              {t('documentsProd.clearHistory', 'vyčistit historii')}
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className='text-xs text-gray-400 mb-3'>
          {t(
            'documentsProd.sandboxDesc',
            'Nahraj libovolný soubor (image/jpeg, image/png, image/webp, application/pdf). Vision LLM (~5-15s) klasifikuje typ. NE extrahuje content, jen rozhodne kam dál. Drží poslední 3 výsledky.',
          )}
        </p>
        <label className='inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded bg-gray-800 hover:bg-gray-700 text-sm text-gray-200'>
          <input
            type='file'
            accept='image/jpeg,image/png,image/webp,application/pdf'
            onChange={handleFile}
            disabled={loading}
            className='hidden'
          />
          <FileText size={14} />
          {loading
            ? t('documentsProd.classifying', 'Klasifikuji…')
            : t('documentsProd.chooseFile', 'Vyber soubor')}
        </label>

        {history.length > 0 && (
          <div className='mt-4 space-y-3'>
            {history.map((h, idx) => (
              <ClassifyHistoryItem key={h.id} entry={h} isLatest={idx === 0} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ClassifyHistoryItem({ entry, isLatest }: { entry: HistoryEntry; isLatest: boolean }) {
  const { t } = useTranslation();
  const { result, error, fileName, fileSize, ts } = entry;
  const ageSec = Math.floor((Date.now() - ts) / 1000);
  const ago = t('documentsProd.agoSuffix', 'zpět');
  const ageStr = ageSec < 60
    ? `${ageSec}s ${ago}`
    : ageSec < 3600
    ? `${Math.floor(ageSec / 60)}m ${ago}`
    : `${Math.floor(ageSec / 3600)}h ${ago}`;

  return (
    <div className={`p-3 rounded border ${isLatest ? 'border-gray-700 bg-gray-900/40' : 'border-gray-800 bg-gray-900/20'}`}>
      <div className='flex items-center justify-between mb-2 flex-wrap gap-2'>
        <div className='text-xs text-gray-300 font-mono'>
          {fileName} <span className='text-gray-500'>({(fileSize / 1024).toFixed(1)} KB)</span>
        </div>
        <div className='text-[10px] text-gray-500 font-mono'>
          {isLatest && (
            <span className='mr-2 px-1.5 py-0.5 rounded bg-blue-900/40 text-blue-300'>
              {t('documentsProd.latestBadge', 'latest')}
            </span>
          )}
          {ageStr}
        </div>
      </div>

      {error && (
        <div className='p-2 rounded bg-red-950/30 border border-red-900 text-xs text-red-300'>
          <strong>{t('documentsProd.errorLabel', 'Chyba:')}</strong> {error}
        </div>
      )}

      {result && (
        <div className='space-y-2'>
          <div className='flex items-center gap-4 text-sm flex-wrap'>
            <Badge variant={result.supported ? 'green' : result.doc_type === 'UNKNOWN' ? 'gray' : 'yellow'} className='font-mono'>
              {result.doc_type}
              {result.doc_subtype && ` / ${result.doc_subtype}`}
            </Badge>
            {result.language && <Badge variant='gray' className='text-[10px]'>{result.language}</Badge>}
            {result.is_scan && (
              <Badge variant='yellow' className='text-[10px]'>{t('documentsProd.scanBadge', 'scan')}</Badge>
            )}
            <span className='text-xs text-gray-500'>
              {t('documentsProd.confLabel', 'conf')} {(result.confidence * 100).toFixed(0)}% ·{' '}
              {result.vision_duration_ms ?? '?'}ms {t('documentsProd.visionWord', 'vision')}
            </span>
          </div>
          {result.supported && result.recommended_tool && (
            <div className='text-xs text-green-300'>
              {t('documentsProd.routeToLabel', '→ routovat na')}{' '}
              <span className='font-mono'>{result.recommended_tool}</span>
            </div>
          )}
          {!result.supported && result.doc_type !== 'UNKNOWN' && (
            <div className='text-xs text-yellow-300'>
              {t(
                'documentsProd.noParserFallback',
                'Známý typ ale ACM nemá deterministic parser. Lovable musí fallback na Gemini nebo manual review.',
              )}
            </div>
          )}
          {result.issuer_hint && (
            <div className='text-xs text-gray-400'>
              {t('documentsProd.issuerLabel', 'Vystavitel:')}{' '}
              <span className='font-mono'>{result.issuer_hint}</span>
            </div>
          )}
          {result.description && (
            <div className='text-xs text-gray-300 p-2 rounded bg-gray-900/40 border border-gray-800'>
              <strong className='text-gray-500'>{t('documentsProd.descriptionColonLabel', 'Description:')}</strong>{' '}
              {result.description}
            </div>
          )}
          <details className='text-[11px] text-gray-500'>
            <summary className='cursor-pointer hover:text-gray-300'>
              {t('documentsProd.rawJsonCap', 'Raw JSON')}
            </summary>
            <pre className='mt-2 bg-gray-950 border border-gray-800 rounded p-2 overflow-x-auto'>
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}

export default function DocumentsProdPage() {
  const { t } = useTranslation();
  const [window, setWindow] = useState<'24h' | '7d' | '30d'>('24h');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [anomalyFilter, setAnomalyFilter] = useState<string | null>(null);
  const [toolFilter, setToolFilter] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [expandedTool, setExpandedTool] = useState<string | null>(null);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['documents_prod', 'stats', window, statusFilter, anomalyFilter, toolFilter],
    queryFn: () =>
      api.mcp<StatsResponse>('documents_prod.stats', {
        window,
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(anomalyFilter ? { anomaly_code: anomalyFilter } : {}),
        ...(toolFilter ? { tool: toolFilter } : {}),
      }),
    refetchInterval: 30_000,
  });

  const total = data?.total_calls ?? 0;
  const okCount = data?.status_summary.OK ?? 0;
  const anomalyCount = (data?.status_summary.ANOMALY ?? 0) + (data?.status_summary.FAIL ?? 0);
  const warnCount = data?.status_summary.WARN ?? 0;
  const unknownCount = data?.status_summary.UNKNOWN ?? 0;
  // Success rate jen z calls s validním status (UNKNOWN = legacy audit entries
  // z doby pre-anomaly-contract, nepočítáme do jmenovatele)
  const statusedCount = okCount + warnCount + anomalyCount;
  const successRate = statusedCount ? Math.round((okCount / statusedCount) * 100) : 0;

  const lat = data?.latency_by_category;

  return (
    <div className='space-y-6'>
      <PageHeader
        title='Documents PROD'
        description={t(
          'documentsProd.pageDescription',
          'Production document parsers — výpisy, daňová přiznání, BRKI/NRKI, ID doklady, LV, ARES. Volání za posledních {{window}}.',
          {
            window: window === '24h'
              ? t('documentsProd.window24h', '24 hodin')
              : window === '7d'
              ? t('documentsProd.window7d', '7 dní')
              : t('documentsProd.window30d', '30 dní'),
          },
        )}
        actions={
          <div className='flex items-center gap-2'>
            <div className='flex rounded-md border border-gray-700 bg-gray-900'>
              {(['24h', '7d', '30d'] as const).map((w) => (
                <button
                  key={w}
                  onClick={() => setWindow(w)}
                  className={`px-3 py-1.5 text-xs font-medium ${
                    window === w
                      ? 'bg-gray-700 text-white'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {w}
                </button>
              ))}
            </div>
            <RefreshButton onRefresh={() => refetch()} loading={isFetching} />
          </div>
        }
      />

      {/* ── Top stats ─────────────────────────────────────── */}
      <StatsGrid>
        <button
          onClick={() => setStatusFilter(null)}
          className='text-left'
          title={t('documentsProd.totalCallsTooltip', 'Vidíš celkem za vybrané období')}
        >
          <StatCard
            label={t('documentsProd.totalCallsLabel', 'Celkem volání')}
            value={total}
            icon={Activity}
            sub={unknownCount > 0
              ? t('documentsProd.withoutStatusSub', '{{count}} bez statusu', { count: unknownCount })
              : undefined}
          />
        </button>
        <button
          onClick={() => setStatusFilter('OK')}
          className='text-left'
          title={t('documentsProd.filterOkTooltip', 'Klik = filtr na OK')}
        >
          <StatCard
            label={t('documentsProd.successRateLabel', 'Success rate (OK)')}
            value={`${successRate}%`}
            sub={statusedCount
              ? t('documentsProd.validatedSub', '{{ok}}/{{total}} validovaných', {
                ok: okCount,
                total: statusedCount,
              })
              : t('documentsProd.noneValidated', 'žádná validovaná')}
            icon={CheckCircle}
            variant={successRate >= 95 ? 'success' : successRate >= 80 ? 'warning' : 'error'}
          />
        </button>
        <button
          onClick={() => setStatusFilter(statusFilter === 'ANOMALY' ? null : 'ANOMALY')}
          className='text-left'
          title={t('documentsProd.filterAnomalyTooltip', 'Klik = filtr na ANOMALY')}
        >
          <StatCard
            label={t('documentsProd.anomalyFailLabel', 'Anomálie / FAIL')}
            value={anomalyCount}
            icon={AlertTriangle}
            variant={anomalyCount === 0 ? 'success' : 'warning'}
            sub={warnCount > 0
              ? t('documentsProd.plusWarnSub', '+ {{count}} WARN', { count: warnCount })
              : undefined}
          />
        </button>
        <StatCard
          label={t('documentsProd.textParserP95Label', 'Text parser p95')}
          value={lat?.text.count ? fmtMs(lat.text.p95_ms) : '–'}
          sub={lat?.text.count
            ? t('documentsProd.callsAvgSub', '{{count}} volání · avg {{avg}}', {
              count: lat.text.count,
              avg: fmtMs(lat.text.avg_ms),
            })
            : t('documentsProd.noneWord', 'žádná')}
          icon={Clock}
        />
        <StatCard
          label={t('documentsProd.visionParserP95Label', 'Vision parser p95')}
          value={lat?.vision.count ? fmtMs(lat.vision.p95_ms) : '–'}
          sub={lat?.vision.count
            ? t('documentsProd.callsAvgSub', '{{count}} volání · avg {{avg}}', {
              count: lat.vision.count,
              avg: fmtMs(lat.vision.avg_ms),
            })
            : t('documentsProd.noneWord', 'žádná')}
          icon={Clock}
        />
        <StatCard
          label={t('documentsProd.apiLookupP95Label', 'API lookup p95')}
          value={lat?.api.count ? fmtMs(lat.api.p95_ms) : '–'}
          sub={lat?.api.count
            ? t('documentsProd.callsAvgSub', '{{count}} volání · avg {{avg}}', {
              count: lat.api.count,
              avg: fmtMs(lat.api.avg_ms),
            })
            : t('documentsProd.noneWord', 'žádná')}
          icon={Clock}
        />
      </StatsGrid>

      {isLoading && (
        <div className='text-sm text-gray-500'>
          {t('documentsProd.loadingStats', 'Načítám statistiky…')}
        </div>
      )}

      <Tabs defaultTab='monitoring'>
        <TabList>
          <Tab id='monitoring' icon={<LayoutDashboard size={14} />}>
            {t('documentsProd.tabMonitoring', 'Monitoring')}
          </Tab>
          <Tab id='docs' icon={<BookOpen size={14} />}>
            {t('documentsProd.tabDocs', 'Dokumentace')}
          </Tab>
        </TabList>

        <TabPanel id='docs'>
          <div className='space-y-6'>
            <ConnectionInfoPanel />
            <AvailableParsersPanel />
          </div>
        </TabPanel>

        <TabPanel id='monitoring'>
          <div className='space-y-6'>

      {/* ── Per-tool breakdown ─────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('documentsProd.toolsTableTitle', 'Tools — volume, latence, status')}</CardTitle>
        </CardHeader>
        <CardContent>
          {data?.by_tool.length ? (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead className='border-b border-gray-800 text-left text-xs uppercase text-gray-500'>
                  <tr>
                    <th className='py-2'>{t('documentsProd.colTool', 'Tool')}</th>
                    <th className='py-2 text-right'>{t('documentsProd.colCalls', 'Volání')}</th>
                    <th className='py-2 text-right'>p50</th>
                    <th className='py-2 text-right'>p95</th>
                    <th className='py-2 text-right'>p99</th>
                    <th className='py-2'>{t('documentsProd.colStatus', 'Status')}</th>
                    <th className='py-2'>{t('documentsProd.colTopAnomalies', 'Top anomálie')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_tool.map((toolStat) => (
                    <Fragment key={toolStat.tool}>
                    <tr
                      className='border-b border-gray-900 hover:bg-gray-900/30 cursor-pointer'
                      onClick={() => setExpandedTool(expandedTool === toolStat.tool ? null : toolStat.tool)}
                      title={t('documentsProd.rowDetailTooltip', 'Klik = detail posledního dokumentu')}
                    >
                      <td className='py-2 font-mono text-xs'>
                        <span className='inline-block w-3 text-gray-500'>
                          {expandedTool === toolStat.tool ? '▼' : '▶'}
                        </span>
                        <FileText size={12} className='inline mr-1 text-gray-500' />
                        {toolDisplayName(toolStat.tool)}
                        {toolStat.category === 'vision' && (
                          <span
                            className='ml-1 text-[9px] text-purple-400'
                            title={t('documentsProd.visionCallTooltip', 'Vision LLM call (~7-20s)')}
                          >
                            {t('documentsProd.visionWord', 'vision')}
                          </span>
                        )}
                        {toolStat.category === 'api' && (
                          <span
                            className='ml-1 text-[9px] text-blue-400'
                            title={t('documentsProd.apiLookupTooltip', 'HTTP API lookup')}
                          >
                            {t('documentsProd.apiWord', 'api')}
                          </span>
                        )}
                      </td>
                      <td className='py-2 text-right font-mono'>{toolStat.count}</td>
                      <td className='py-2 text-right font-mono text-xs text-gray-400'>{fmtMs(toolStat.p50_ms)}</td>
                      <td className='py-2 text-right font-mono text-xs text-gray-400'>{fmtMs(toolStat.p95_ms)}</td>
                      <td
                        className='py-2 text-right font-mono text-xs text-gray-400'
                        title={toolStat.count < 5
                          ? t('documentsProd.p99NoSenseTooltip', 'p99 nedává smysl při {{count}} voláních', {
                            count: toolStat.count,
                          })
                          : undefined}
                      >
                        {toolStat.count < 5 ? <span className='text-gray-600'>n/a</span> : fmtMs(toolStat.p99_ms)}
                      </td>
                      <td className='py-2'><StatusBar counts={toolStat.status} /></td>
                      <td className='py-2'>
                        {toolStat.top_anomalies.length > 0 ? (
                          <div className='flex flex-wrap gap-1'>
                            {toolStat.top_anomalies.slice(0, 3).map((a) => (
                              <button
                                key={a.code}
                                onClick={(e) => { e.stopPropagation(); setAnomalyFilter(a.code); }}
                                title={t('documentsProd.filterRecentTooltip', 'Filtruj recent na {{code}}', {
                                  code: a.code,
                                })}
                                className='cursor-pointer'
                              >
                                <Badge variant='red' className='font-mono text-[10px] hover:bg-red-900/40'>
                                  {a.code} ×{a.count}
                                </Badge>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span className='text-xs text-gray-600'>—</span>
                        )}
                      </td>
                    </tr>
                    {expandedTool === toolStat.tool && (
                      <tr className='bg-gray-900/40 border-b border-gray-900'>
                        <td colSpan={7} className='px-4 py-3'>
                          <div className='flex items-center justify-between mb-2'>
                            <div className='text-xs font-semibold text-gray-300'>
                              {t('documentsProd.lastDocumentLabel', 'Poslední dokument —')}{' '}
                              {toolDisplayName(toolStat.tool)}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setToolFilter(toolFilter === toolStat.tool ? null : toolStat.tool);
                              }}
                              className='text-[10px] px-2 py-1 rounded bg-blue-900/40 text-blue-300 hover:bg-blue-900/60 font-mono'
                              title={t('documentsProd.filterRecentActivityTooltip', 'Filtruj recent activity na tento tool')}
                            >
                              {toolFilter === toolStat.tool
                                ? t('documentsProd.unfilterRecent', 'odfiltrovat recent')
                                : t('documentsProd.filterRecent', 'filtruj recent ↓')}
                            </button>
                          </div>
                          <ToolLatestDetail tool={toolStat.tool} recent={data.recent} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={<FileText size={32} />}
              title={t('documentsProd.noCallsYetTitle', 'Zatím žádná volání')}
              description={t(
                'documentsProd.noCallsYetDesc',
                'V okně {{window}} žádné požadavky na documents_prod.* tools.',
                { window },
              )}
            />
          )}
        </CardContent>
      </Card>

      {/* ── Classify vision sandbox ────────────────────────── */}
      <ClassifyVisionPanel />

      {/* ── Recent activity ────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between flex-wrap gap-2'>
            <CardTitle>
              {toolFilter
                ? t('documentsProd.last10ForTool', 'Last 10 — {{tool}}', {
                  tool: toolFilter.replace('documents_prod_', '').replace('documents_', ''),
                })
                : t('documentsProd.recentActivityTitle', 'Recent activity')}
            </CardTitle>
            <div className='flex items-center gap-2 flex-wrap'>
              {toolFilter && (
                <button
                  onClick={() => setToolFilter(null)}
                  className='px-2 py-1 text-[10px] rounded bg-blue-900/40 text-blue-300 hover:bg-blue-900/60 font-mono'
                >
                  {t('documentsProd.toolFilterChip', 'tool: {{tool}} ×', {
                    tool: toolFilter.replace('documents_prod_', '').replace('documents_', ''),
                  })}
                </button>
              )}
              {anomalyFilter && (
                <button
                  onClick={() => setAnomalyFilter(null)}
                  className='px-2 py-1 text-[10px] rounded bg-red-900/40 text-red-300 hover:bg-red-900/60 font-mono'
                  title={t('documentsProd.removeAnomalyFilterTooltip', 'Klik pro odstranění anomaly filtru')}
                >
                  {t('documentsProd.anomalyFilterChip', 'anomaly: {{code}} ×', { code: anomalyFilter })}
                </button>
              )}
              {statusFilter && (
                <button
                  onClick={() => setStatusFilter(null)}
                  className='px-2 py-1 text-[10px] rounded bg-yellow-900/40 text-yellow-300 hover:bg-yellow-900/60 font-mono'
                >
                  {t('documentsProd.statusFilterChip', 'status: {{status}} ×', { status: statusFilter })}
                </button>
              )}
              <div className='flex gap-1'>
                {(['OK', 'WARN', 'ANOMALY', 'FAIL'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(statusFilter === s ? null : s)}
                    className={`px-2 py-1 text-[10px] rounded font-mono ${
                      statusFilter === s
                        ? 'bg-gray-700 text-white'
                        : 'bg-gray-900 text-gray-500 hover:bg-gray-800'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {data?.recent.length ? (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead className='border-b border-gray-800 text-left text-xs uppercase text-gray-500'>
                  <tr>
                    <th className='py-2 w-8'></th>
                    <th className='py-2'>{t('documentsProd.colTime', 'Čas')}</th>
                    <th className='py-2'>{t('documentsProd.colTool', 'Tool')}</th>
                    <th className='py-2'>{t('documentsProd.colStatus', 'Status')}</th>
                    <th className='py-2 text-right'>{t('documentsProd.colLatency', 'Latence')}</th>
                    <th className='py-2'>{t('documentsProd.colDetail', 'Detail')}</th>
                    <th className='py-2'>{t('documentsProd.colSubmitter', 'Odeslal')}</th>
                    <th className='py-2'>{t('documentsProd.colAnomalies', 'Anomálie')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.slice(0, toolFilter ? 10 : 50).map((r, i) => (
                    <>
                      <tr
                        key={`row-${i}`}
                        className='border-b border-gray-900 hover:bg-gray-900/30 cursor-pointer'
                        onClick={() => setExpandedRow(expandedRow === i ? null : i)}
                      >
                        <td className='py-2 text-xs text-gray-500 text-center'>
                          {expandedRow === i ? '▼' : '▶'}
                        </td>
                        <td className='py-2 text-xs text-gray-400 whitespace-nowrap'>{fmtAge(r.ts)}</td>
                        <td className='py-2 font-mono text-xs'>{toolDisplayName(r.tool)}</td>
                        <td className='py-2'>
                          <Badge variant={STATUS_COLORS[r.status] ?? 'gray'} className='text-[10px]'>
                            {r.status}
                          </Badge>
                        </td>
                        <td className='py-2 text-right font-mono text-xs text-gray-400'>{fmtMs(r.duration_ms)}</td>
                        <td className='py-2 text-xs text-gray-400'>
                          {r.bank_id ? <span className='font-mono'>{String(r.bank_id)}</span> : null}
                          {r.doc_type ? <span className='font-mono'>{String(r.doc_type)}</span> : null}
                          {r.ico ? <span className='font-mono'>{t('documentsprod.ico', 'IČO')} {String(r.ico)}</span> : null}
                          {r.holder ? <span className='ml-1'>{String(r.holder)}</span> : null}
                        </td>
                        <td className='py-2 text-xs text-gray-400'>
                          {r.submitter ? (
                            <span title={r.submitter_role ?? undefined}>
                              <span>{String(r.submitter)}</span>
                              {r.submitter_role && (
                                <span className='ml-1 text-[10px] text-gray-500'>({r.submitter_role})</span>
                              )}
                            </span>
                          ) : (
                            <span className='text-gray-600'>—</span>
                          )}
                        </td>
                        <td className='py-2'>
                          {r.anomaly_codes.length > 0 ? (
                            <div className='flex flex-wrap gap-1'>
                              {r.anomaly_codes.slice(0, 2).map((c) => (
                                <button
                                  key={c}
                                  onClick={(e) => { e.stopPropagation(); setAnomalyFilter(c); }}
                                  title={t('documentsProd.filterFeedTooltip', 'Filtruj feed na {{code}}', { code: c })}
                                  className='cursor-pointer'
                                >
                                  <Badge variant='red' className='font-mono text-[10px] hover:bg-red-900/40'>{c}</Badge>
                                </button>
                              ))}
                              {r.anomaly_codes.length > 2 && (
                                <span className='text-xs text-gray-500'>+{r.anomaly_codes.length - 2}</span>
                              )}
                            </div>
                          ) : (
                            <span className='text-xs text-gray-600'>—</span>
                          )}
                        </td>
                      </tr>
                      {expandedRow === i && (
                        <tr key={`detail-${i}`} className='bg-gray-900/50'>
                          <td colSpan={8} className='py-3 px-4'>
                            <div className='text-xs space-y-3'>
                              <div className='font-mono text-gray-500'>
                                {new Date(r.ts).toLocaleString('cs')} • {r.tool}
                                {r.identity && (
                                  <span className='ml-3 text-gray-600'>
                                    {t('documentsProd.callerLabel', 'caller:')} {JSON.stringify(r.identity)}
                                  </span>
                                )}
                              </div>
                              {r.anomaly_codes.length > 0 && (
                                <div className='space-y-1'>
                                  <div className='text-[10px] uppercase text-gray-500'>
                                    {t('documentsProd.colAnomalies', 'Anomálie')}
                                  </div>
                                  {r.anomaly_codes.map((c) => (
                                    <button
                                      key={c}
                                      onClick={() => setAnomalyFilter(c)}
                                      className='block w-full text-left text-[11px] font-mono px-2 py-1 rounded bg-red-950/30 text-red-300 hover:bg-red-900/40'
                                    >
                                      {t('documentsProd.anomalyFilterAllSame', '→ {{code}} (klik = filtr na všechny stejné)', {
                                        code: c,
                                      })}
                                    </button>
                                  ))}
                                </div>
                              )}
                              <details>
                                <summary className='cursor-pointer text-[10px] uppercase text-gray-500 hover:text-gray-300'>
                                  {t('documentsProd.auditDataRawJson', 'Audit data (raw JSON)')}
                                </summary>
                                <pre className='mt-2 bg-gray-950 border border-gray-800 rounded p-3 overflow-x-auto text-[11px] text-gray-300'>
                                  {JSON.stringify(r.data ?? {}, null, 2)}
                                </pre>
                              </details>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={<Search size={32} />}
              title={statusFilter
                ? t('documentsProd.noRecordsWithStatus', 'Žádné záznamy se statusem {{status}}', {
                  status: statusFilter,
                })
                : t('documentsProd.noRecentActivity', 'Žádná recent activity')}
              description={statusFilter
                ? t('documentsProd.tryDifferentFilter', 'Zkus jiný filtr nebo počkej na další volání.')
                : t('documentsProd.historyAfterFirstCall', 'Po prvním volání zde uvidíš historii.')}
            />
          )}
        </CardContent>
      </Card>
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
}
